import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/HomePage.css';

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [featured, setFeatured] = useState([]);
  const [contact, setContact] = useState({ name: '', email: '', message: '' });
  const [contactMessage, setContactMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const response = await fetch('/api/shop/products?per_page=4&sort_by=newest');
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        setFeatured(data.products || []);
      } catch (error) {
        setFeatured([]);
      }
    };
    loadFeatured();
  }, []);

  const handleNewsletterSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setNewsletterMessage('');
    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Subscription failed');
      }
      setEmail('');
      setNewsletterMessage(data.message);
    } catch (error) {
      setNewsletterMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSubmit = async event => {
    event.preventDefault();
    setContactMessage('');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contact),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Could not send your message');
      }
      setContact({ name: '', email: '', message: '' });
      setContactMessage(data.message);
    } catch (error) {
      setContactMessage(error.message);
    }
  };

  // For navigation
  const handleNavigate = path => {
    navigate(path);
  };

  return (
    <div className='homepage'>
      <main className='main-content'>
        {/* Hero Section */}
        <section className='hero-section' aria-label='Welcome'>
          <div className='hero-overlay'></div>
          <div className='container hero-content'>
            <div className='hero-text-container'>
              <h1 className='hero-title'>
                <span className='brand-name'>AmFashion</span>
                <span className='brand-tagline'>Where Style Meets Elegance</span>
              </h1>
              <p className='hero-subtitle'>
                Discover luxury fashion that defines your unique style. From timeless classics to
                contemporary trends, find pieces that make every occasion extraordinary.
              </p>
              <div className='hero-actions'>
                <button
                  onClick={() => handleNavigate('/shop')}
                  className='shop-now-btn primary'
                  aria-label='Shop Now'
                >
                  Explore Collection
                </button>
                <button
                  onClick={() => handleNavigate('/customize-outfit')}
                  className='shop-now-btn secondary'
                  aria-label='Create Custom Outfit'
                >
                  Style Studio
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className='coming-soon' aria-label='Shop by category'>
          <div className='container'>
            <h2>Shop by Category</h2>
            <div className='coming-soon-cards'>
              {[
                ['Dresses', 'Evening and daytime dresses.'],
                ['Tops', 'Blouses and shirts for every plan.'],
                ['Bottoms', 'Jeans and skirts with a clean fit.'],
                ['Outerwear', 'Jackets and blazers to finish the look.'],
              ].map(([item, copy]) => (
                <div className='coming-soon-card' key={item}>
                  <button
                    onClick={() => handleNavigate(`/shop?category=${item}`)}
                    className='card-content'
                    aria-label={`Shop ${item}`}
                  >
                    <h3>{item}</h3>
                    <p>{copy}</p>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {featured.length > 0 && (
          <section className='featured-section' aria-label='Featured products'>
            <div className='container'>
              <h2>Featured</h2>
              <div className='featured-grid'>
                {featured.map(product => (
                  <Link key={product.id} to={`/product/${product.id}`} className='featured-card'>
                    <img src={product.image_url} alt={product.name} />
                    <h3>{product.name}</h3>
                    <p>${Number(product.price).toFixed(2)}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Divider */}
        <div className='section-divider'></div>

        {/* Stay Updated Section */}
        <section className='stay-updated-section' aria-label='Newsletter'>
          <div className='container'>
            <h2>Stay Updated</h2>
            <p>
              Sign up for our newsletter and be the first to know about new collections and offers.
            </p>
            <form className='newsletter-form' onSubmit={handleNewsletterSubmit}>
              <input
                type='email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder='Enter your email'
                required
                aria-label='Email for newsletter'
              />
              <button type='submit' disabled={isLoading}>
                {isLoading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {newsletterMessage && <p>{newsletterMessage}</p>}
          </div>
        </section>

        {/* Contact Us Section */}
        <section className='contact-us-section'>
          <div className='container'>
            <h2>Contact Us</h2>
            <p>
              We&apos;d love to hear from you! Reach out to us with your queries, feedback, or
              concerns.
            </p>
            <form className='contact-form' onSubmit={handleContactSubmit}>
              <input
                type='text'
                placeholder='Your Name'
                value={contact.name}
                onChange={event => setContact(current => ({ ...current, name: event.target.value }))}
                required
              />
              <input
                type='email'
                placeholder='Your Email'
                value={contact.email}
                onChange={event => setContact(current => ({ ...current, email: event.target.value }))}
                required
              />
              <textarea
                placeholder='Your Message'
                rows='5'
                value={contact.message}
                onChange={event => setContact(current => ({ ...current, message: event.target.value }))}
                required
              />
              <button type='submit'>Send Message</button>
            </form>
            {contactMessage && <p>{contactMessage}</p>}
          </div>
        </section>
      </main>

    </div>
  );
};

export default HomePage;
